// src/KeycodeGenerator.js
import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { Container, Card, Button, Alert, Spinner } from 'react-bootstrap';

const KeycodeGenerator = ({ user }) => {
    const [dispensedKeycode, setDispensedKeycode] = useState('');
    const [unusedCount, setUnusedCount] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingCount, setLoadingCount] = useState(true);
    const [isDispensing, setIsDispensing] = useState(false); // New state for dispensing loading

    // Effect to update the count when user status changes or component mounts
    useEffect(() => {
        if (user) {
            updateUnusedCount();
        } else {
            setUnusedCount(0);
            setLoadingCount(false);
        }
    }, [user]);

    // Effect to trigger print dialog when a keycode is dispensed
    useEffect(() => {
        if (dispensedKeycode) {
            const printTimeout = setTimeout(() => {
                const printWindow = window.open('', '', 'height=400,width=600');
                if (printWindow) {
                    printWindow.document.write('<html><head><title>Keycode Print</title>');
                    printWindow.document.write('</head><body>');
                    printWindow.document.write('<b>30 päivää 100Mbps</b>');
                    printWindow.document.write('<p>WLAN-koodi:<br/>');
                    printWindow.document.write(`${dispensedKeycode}</p>`);
                    printWindow.document.write(`<p>Syötä koodi kirjautumissivun <br/>Acces Code -ruutuun.</p>`);
                    printWindow.document.write('</body></html>');
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                    setDispensedKeycode(''); // Clear the dispensed keycode after printing
                } else {
                    setErrorMessage("Pop-up blocked. Please allow pop-ups for this site to print.");
                }
            }, 100);

            return () => clearTimeout(printTimeout);
        }
    }, [dispensedKeycode]);

    const updateUnusedCount = async () => {
        setLoadingCount(true);
        setErrorMessage('');
        try {
            const q = query(collection(db, 'keycodes'), where('used', '==', false));
            const querySnapshot = await getDocs(q);
            setUnusedCount(querySnapshot.size);
        } catch (error) {
            console.error("Error getting unused keycode count:", error);
            setErrorMessage("Error loading unused keycode count.");
        } finally {
            setLoadingCount(false);
        }
    };

    const getKeycode = async () => {
        if (!user) {
            setErrorMessage("Please log in to get a keycode.");
            return;
        }

        setDispensedKeycode('');
        setErrorMessage('');
        setIsDispensing(true); // Start dispensing loading

        try {
            const q = query(collection(db, 'keycodes'), where('used', '==', false));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                const docToUse = querySnapshot.docs[0];
                const keycodeData = docToUse.data();
                const keycodeId = docToUse.id;

                const keycodeRef = doc(db, 'keycodes', keycodeId);
                await updateDoc(keycodeRef, { used: true });

                setDispensedKeycode(keycodeData.code);
                await updateUnusedCount();
            } else {
                setErrorMessage("No unused keycodes available at the moment.");
            }
        } catch (error) {
            console.error("Error fetching or updating keycode:", error);
            if (error.code === 'permission-denied') {
                setErrorMessage("Permission Denied: Make sure your Firebase security rules allow read/write for authenticated users.");
            } else {
                setErrorMessage(`An error occurred: ${error.message}`);
            }
        } finally {
            setIsDispensing(false); // End dispensing loading
        }
    };

    return (
        <Container className="my-4">
            <Card className="p-4 shadow-sm">
                <Card.Body className="text-center">
                    <Card.Title className="mb-3">Koodin jako</Card.Title>
                    {!user && <Alert variant="info" className="mb-3">Kirjaudupa sisään.</Alert>}
                    <Button
                        variant="success"
                        onClick={getKeycode}
                        disabled={!user || loadingCount || isDispensing}
                        className="mb-3"
                    >
                        {isDispensing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />{' '}
                                Tulostetaan...
                            </>
                        ) : (
                            'Tulosta 100Mbps/1kk koodi'
                        )}
                    </Button>
                    {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                    <Card.Text className="mt-3">
                        Koodeja käyttämättä: {loadingCount ? <Spinner animation="border" size="sm" /> : unusedCount}
                    </Card.Text>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default KeycodeGenerator;